import { useState, useEffect, useCallback } from 'react';

const isDev = process.env.NODE_ENV !== 'production';

/**
 * Custom hook for managing Wake Lock API to prevent screen from sleeping
 * Perfect for driver apps where screen needs to stay active
 */
export const useWakeLock = () => {
  const [wakeLock, setWakeLock] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState(null);

  // Check if Wake Lock API is supported
  useEffect(() => {
    setIsSupported('wakeLock' in navigator);
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!isSupported) {
      setError('Wake Lock API is not supported in this browser');
      return false;
    }

    // A screen wake lock can only be acquired while the page is visible.
    // Requesting it while hidden (tab backgrounded, DevTools focused, or during
    // the initial load before the page paints) throws NotAllowedError
    // ("The requesting page is not visible"). Skip quietly here — the
    // visibilitychange handler below re-requests once the page is visible.
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return false;
    }

    try {
      const lock = await navigator.wakeLock.request('screen');
      setWakeLock(lock);
      setIsActive(true);
      setError(null);

      // Handle wake lock release (can happen automatically)
      lock.addEventListener('release', () => {
        if (isDev) console.log('Wake lock was released - will attempt to re-acquire');
        setIsActive(false);
        setWakeLock(null);
      });

      if (isDev) console.log('Wake lock acquired - screen will not sleep');
      return true;
    } catch (err) {
      // NotAllowedError/AbortError are benign (page not visible / interrupted)
      // and not actionable — don't surface them as an error so they stop
      // polluting the console. Real failures still log + set error.
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        if (isDev) console.debug('Wake lock not granted:', err.message || err.name);
        return false;
      }
      console.error('Failed to request wake lock:', err);
      setError(err.message);
      return false;
    }
  }, [isSupported]);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLock) {
      try {
        await wakeLock.release();
        setWakeLock(null);
        setIsActive(false);
        setError(null);
        console.log('Wake lock released');
        return true;
      } catch (err) {
        console.error('Failed to release wake lock:', err);
        setError(err.message);
        return false;
      }
    }
    return true;
  }, [wakeLock]);

  // Auto-request wake lock when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && isSupported && !isActive) {
        // Page became visible and no active wake lock, request one
        if (isDev) console.log('Page became visible - re-requesting wake lock');
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSupported, isActive, requestWakeLock]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wakeLock) {
        wakeLock.release();
      }
    };
  }, [wakeLock]);

  return {
    isSupported,
    isActive,
    error,
    requestWakeLock,
    releaseWakeLock
  };
};